import { type ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Ảnh đại diện bằng **chữ đầu của tên** — APM-LIST-a.
 *
 * ## Vì sao không phải ảnh thật
 *
 * Hệ thống chưa có ảnh đại diện ở đâu cả, và thêm một chỗ tải ảnh chỉ để trang
 * trí là thêm một đường đi vào S3 (luật #3) cho một việc mà hai chữ cái làm
 * xong. Khi nào có ảnh thật thì component này là chỗ duy nhất phải sửa.
 *
 * ## Vì sao "chưa giao" vẫn chiếm chỗ
 *
 * Một ô trống và một ô "chưa giao cho ai" nhìn giống hệt nhau, nhưng chỉ cái
 * thứ hai là thông tin. Vòng tròn nét đứt nói ra điều đó mà không tốn chữ nào,
 * và nó giữ cho cột không so le giữa hàng có người và hàng chưa có.
 *
 * ## ⚠️ Bảng màu viết sẵn, KHÔNG ghép lúc chạy
 *
 * Tailwind quét mã nguồn, nên `'bg-tag-' + n` cho ra một lớp không tồn tại:
 * avatar hiện đúng chữ, đúng kích thước, và **trong suốt**.
 */
const TONE = [
  'bg-tag-1/15 text-tag-1',
  'bg-tag-2/15 text-tag-2',
  'bg-tag-3/15 text-tag-3',
  'bg-tag-4/15 text-tag-4',
  'bg-tag-5/15 text-tag-5',
  'bg-tag-6/15 text-tag-6',
  'bg-tag-7/15 text-tag-7',
  'bg-tag-8/15 text-tag-8',
] as const;

const SIZE = {
  sm: 'size-6 text-[10px]',
  md: 'size-8 text-xs',
} as const;

/** FNV-1a — cùng thuật toán với `projectTone`, để hai chỗ không lệch nhau. */
function hashOf(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), 16_777_619);
  }
  return hash >>> 0;
}

/** Hai chữ cái đầu của hai từ đầu — `'Bùi Ngọc Lan'` → `BN`. */
export function initialsOf(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter((part) => part.length > 0)
      .slice(0, 2)
      .map((part) => part[0]!.toUpperCase())
      .join('') || '?'
  );
}

export function Avatar({
  name,
  size = 'md',
  className,
}: {
  /** `null` = chưa giao cho ai — vẫn vẽ, bằng vòng tròn nét đứt. */
  readonly name: string | null;
  readonly size?: keyof typeof SIZE;
  readonly className?: string;
}): ReactNode {
  if (name === null || name.trim() === '') {
    return (
      <span
        aria-hidden
        className={cn(
          'inline-block shrink-0 rounded-full border border-dashed border-border',
          SIZE[size],
          className,
        )}
      />
    );
  }

  return (
    <span
      // `title` chứ không phải chữ bên cạnh: ở một bảng nhiều cột, tên đầy đủ
      // của mọi người chiếm mất bề ngang mà chỉ trả lời một câu hỏi hiếm.
      title={name}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-medium',
        TONE[hashOf(name) % TONE.length],
        SIZE[size],
        className,
      )}
    >
      {initialsOf(name)}
    </span>
  );
}
